import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { RENTER_INVITE_QUERY_PARAM, routes } from '@/constants/Constants';
import { usePermission } from '@/hooks/usePermission';
import { UserPermissions } from '@/constants/permissions.constant';
import { navigateInOrder } from '../../../utils/navigation.utils';
import type { InviteNavigationState } from '../types';
import {
  buildInviteNavigationState,
  resolveInviteEntryPoint,
  type OpenInviteSource,
} from '../utils';

const withInviteParam = (search: string) => {
  const params = new URLSearchParams(search);
  params.set(RENTER_INVITE_QUERY_PARAM, 'true');
  return `?${params.toString()}`;
};

/**
 * Opens the invite lease details drawer by pushing `?renter-invite=true`, so the drawer-open state
 * is its own history entry and Back (or closing) peels it off.
 *
 * - On App Tracker, or without App Tracker access → open on the current page.
 * - Elsewhere, with App Tracker access → App Tracker first, then the drawer on top of it.
 */
export const useInviteLeaseDetailsAction = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // The success toast's "Invite another" calls this after the drawer has closed; read that URL.
  const latestSearch = useRef(location.search);
  useLayoutEffect(() => {
    latestSearch.current = location.search;
  });

  const { notSet: appTrackerNotSet, enabled: appTrackerEnabled } = usePermission(
    UserPermissions.Deal.CanAccessDealsDashboard,
  );
  const canAccessAppTracker = appTrackerNotSet || appTrackerEnabled;

  const isOnAppTracker = location.pathname === routes.dealsDashboard;

  const openInviteLeaseDetails = (source?: OpenInviteSource) => {
    const inviteEntryPoint = resolveInviteEntryPoint(isOnAppTracker, source);
    const state: InviteNavigationState = {
      ...buildInviteNavigationState(inviteEntryPoint, source),
      closesWithBack: true,
    };

    if (canAccessAppTracker && !isOnAppTracker) {
      navigateInOrder(
        () => navigate(routes.dealsDashboard),
        () => navigate(`${routes.dealsDashboard}${withInviteParam('')}`, { state }),
      );
      return;
    }

    navigateInOrder(() => navigate({ search: withInviteParam(latestSearch.current) }, { state }));
  };

  return { openInviteLeaseDetails };
};
